import React from 'react';
import { MessageSquare, Zap, Clock, CheckCircle } from 'lucide-react';

/**
 * PerformanceSummary - Hero section with narrative
 * Balanced: Clean card, meaningful status colors
 */
const PerformanceSummary = ({ analytics }) => {
    const conversations = analytics?.messageVolume?.totalConversations || 0;
    const avgMessages = analytics?.conversationMetrics?.avgMessagesPerConversation || 0;
    const responseTime = analytics?.responseTime?.average || 'N/A';
    const resolvedTickets = analytics?.ticketMetrics?.resolvedTickets || 0;
    const openTickets = analytics?.ticketMetrics?.openTickets || 0;

    const getStatus = () => {
        if (openTickets === 0 && conversations > 0) return { label: 'All Caught Up', color: '#10b981' };
        if (openTickets < 10) return { label: 'Running Smoothly', color: '#10b981' };
        if (openTickets < 50) return { label: 'Needs Attention', color: '#f59e0b' };
        return { label: 'Action Required', color: '#ef4444' };
    };

    const status = getStatus();

    const getNarrativeSummary = () => {
        if (conversations === 0) {
            return "No conversations yet this period. Your chatbot is ready.";
        }
        const avgText = avgMessages > 0 ? `, averaging ${avgMessages.toFixed(1)} messages each` : '';
        return `Your chatbot handled ${conversations.toLocaleString()} conversation${conversations !== 1 ? 's' : ''}${avgText}`;
    };

    const statPills = [
        { icon: MessageSquare, value: conversations.toLocaleString(), label: 'Conversations', color: '#8b5cf6' },
        { icon: Zap, value: avgMessages > 0 ? avgMessages.toFixed(1) : '-', label: 'Avg Messages', color: '#3b82f6' },
        { icon: Clock, value: responseTime, label: 'Response', color: '#10b981' }
    ];

    return (
        <div className="bg-white rounded-2xl border border-slate-200/80 p-6">
            {/* Status */}
            <div className="flex items-center gap-2 mb-3">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: status.color }} />
                <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: status.color }}>
                    {status.label}
                </span>
            </div>

            {/* Narrative */}
            <p className="text-base text-slate-700 leading-relaxed mb-4">
                {getNarrativeSummary()}
            </p>

            {/* Resolution */}
            {resolvedTickets > 0 && (
                <p className="text-xs text-slate-500 mb-5 flex items-center gap-1.5">
                    <CheckCircle size={12} className="text-emerald-500" />
                    {resolvedTickets.toLocaleString()} resolved
                </p>
            )}

            {/* Stat Pills - White bg, colored icons */}
            <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
                {statPills.map((stat, index) => (
                    <div key={index} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-100">
                        <stat.icon size={14} style={{ color: stat.color }} />
                        <div>
                            <div className="text-sm font-semibold text-slate-700 leading-none">{stat.value}</div>
                            <div className="text-[9px] text-slate-400 uppercase tracking-wide mt-0.5">{stat.label}</div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default PerformanceSummary;
